/**
 * Loads air dates for the shows that actually have unaired episodes.
 *
 * Air dates live on /tv/episodes/{id}, one request per show. Fetching all 80
 * would be wasteful and slow; only shows Simkl reports as having unaired
 * episodes can contribute to Upcoming, and there are eight of those. The
 * rate limiter serialises the rest.
 */
import { useEffect, useState } from "react";

import type { SimklClient } from "@/api/client";
import type { LibraryShow } from "@/api/schemas";
import { parseEpisodes } from "@/lib/airtime";
import type { UpcomingEpisode } from "@/lib/airtime";

export function useUpcoming(
  client: SimklClient,
  shows: readonly LibraryShow[],
  enabled: boolean,
): { episodes: readonly UpcomingEpisode[]; loading: boolean } {
  const [episodes, setEpisodes] = useState<readonly UpcomingEpisode[]>([]);
  const [loading, setLoading] = useState(false);

  // Key on the ids so re-renders with an equal list do not refetch.
  const airingIds = shows
    .filter((show) => show.notAiredEpisodes > 0)
    .map((show) => show.show.ids.simkl)
    .join(",");

  useEffect(() => {
    if (!enabled || airingIds.length === 0) {
      setEpisodes([]);
      return;
    }
    const controller = new AbortController();
    setLoading(true);

    const byId = new Map(shows.map((show) => [show.show.ids.simkl, show]));
    const ids = airingIds.split(",").map(Number);

    Promise.all(
      ids.map(async (id) => {
        const show = byId.get(id);
        if (!show) return [];
        const raw = await client.episodes(id, controller.signal);
        return parseEpisodes(raw)
          .filter((episode) => !episode.aired && episode.airsAt !== null)
          .map((episode): UpcomingEpisode => ({
            ...episode,
            showId: id,
            showTitle: show.show.title,
            showPoster: show.show.poster,
          }));
      }),
    )
      .then((lists) => {
        if (!controller.signal.aborted) setEpisodes(lists.flat());
      })
      .catch(() => {
        // Upcoming is secondary; a failure here leaves the section empty
        // rather than taking down the screen the user opened.
        if (!controller.signal.aborted) setEpisodes([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- airingIds is the stable key for shows
  }, [client, enabled, airingIds]);

  return { episodes, loading };
}
